/**
 * =============================================================================
 * Helper: Regras de senha forte (compartilhado)
 * Versão: 1.0.0
 *
 * Descrição:
 *   Centraliza as regras de senha forte exigidas pelo backend (Supabase Auth)
 *   para que TODAS as telas de definição/redefinição de senha validem no
 *   cliente ANTES de chamar o servidor, evitando toasts com mensagens cruas
 *   em inglês ("Password should contain at least one character of each...").
 *
 *   Exporta:
 *   - PASSWORD_RULES: lista de regras (id, rótulo PT-BR, teste) para exibir
 *     o checklist em tempo real abaixo do campo de senha.
 *   - strongPasswordSchema: schema zod com mensagens em PT-BR.
 *   - evaluatePasswordRules: avalia quais regras uma senha atende.
 *   - translatePasswordError: traduz mensagens conhecidas do backend para PT-BR.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-04 v1.0.0 - Versão inicial: regras alinhadas ao Supabase
 *            Auth (minúscula, maiúscula, número, caractere especial, mín. 8).
 * =============================================================================
 */
import { z } from 'zod';

/**
 * Regra individual de senha forte.
 * - id: identificador estável da regra
 * - label: rótulo exibido no checklist (PT-BR)
 * - test: função que retorna true quando a senha atende a regra
 */
export interface PasswordRule {
  id: 'min_length' | 'lowercase' | 'uppercase' | 'digit' | 'special';
  label: string;
  test: (password: string) => boolean;
}

/**
 * Lista única de regras — mesma ordem exibida no checklist das telas.
 * Alinhada com a política do Supabase Auth (letras minúsculas, maiúsculas,
 * dígitos e símbolos) + mínimo de 8 caracteres adotado pelo produto.
 */
export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'min_length',
    label: 'Mínimo de 8 caracteres',
    test: (password) => password.length >= 8,
  },
  {
    id: 'lowercase',
    label: 'Uma letra minúscula',
    test: (password) => /[a-z]/.test(password),
  },
  {
    id: 'uppercase',
    label: 'Uma letra maiúscula',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'digit',
    label: 'Um número',
    test: (password) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'Um caractere especial (ex.: !@#$%)',
    test: (password) => /[^a-zA-Z0-9]/.test(password),
  },
];

/**
 * Avalia quais regras a senha informada atende.
 * Retorna array na mesma ordem de PASSWORD_RULES com `met` por regra.
 */
export function evaluatePasswordRules(password: string) {
  return PASSWORD_RULES.map((rule) => ({
    ...rule,
    met: rule.test(password),
  }));
}

/**
 * Schema zod de senha forte com mensagens em PT-BR.
 * Usa superRefine para apontar TODAS as regras pendentes de uma vez,
 * mas expõe a primeira mensagem para os formulários que exibem erro único.
 */
export const strongPasswordSchema = z.string().superRefine((password, ctx) => {
  const pending = PASSWORD_RULES.filter((rule) => !rule.test(password));
  if (pending.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Senha fraca: ${pending.map((rule) => rule.label.toLowerCase()).join(', ')}`,
    });
  }
});

/**
 * Traduz mensagens de erro de senha vindas do backend (inglês) para PT-BR.
 * Rede de segurança: normalmente a validação client-side barra antes, mas se
 * o servidor recusar, o usuário vê uma mensagem amigável.
 */
export function translatePasswordError(message: string | null | undefined): string {
  if (!message) {
    return 'Não foi possível atualizar a senha. Tente novamente.';
  }
  const normalized = message.toLowerCase();
  if (isExpiredResetLinkError(message)) {
    return 'Seu link de redefinição expirou ou já foi usado. Solicite um novo.';
  }
  if (
    normalized.includes('password should contain') ||
    normalized.includes('weak_password') ||
    normalized.includes('weak password')
  ) {
    return 'A senha deve conter letra maiúscula, letra minúscula, número e caractere especial.';
  }
  if (normalized.includes('at least') && normalized.includes('characters')) {
    return 'A senha não atende ao tamanho mínimo exigido.';
  }
  if (normalized.includes('same password') || normalized.includes('different from the old')) {
    return 'A nova senha deve ser diferente da senha atual.';
  }
  return message;
}

/**
 * Indica se o erro corresponde a link de redefinição vencido, já usado
 * ou sessão de recuperação ausente (ex.: "Auth session missing!").
 */
export function isExpiredResetLinkError(message: string | null | undefined): boolean {
  if (!message) return false;
  const n = message.toLowerCase();
  return (
    n.includes('auth session missing') ||
    n.includes('otp_expired') ||
    n.includes('invalid or has expired') ||
    n.includes('token not found') ||
    n.includes('access_denied')
  );
}
